/**
 * CloudGMBusinessArchive — archive hub for CloudGM Business platform.
 * Three cards: Personal Stores Archive + Company Stores Archive + Archived Accounts.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { User, Building2, Users } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';
import { usePerms } from '../../permissions/PermissionsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CloudGMBusinessArchive() {
  const navigate = useNavigate();
  const { visiblePages } = usePerms();
  const { language } = useLanguage();
  const [counts, setCounts] = useState({ personal: 0, company: 0, total: 0 });
  const [accountsCount, setAccountsCount] = useState(0);

  const loadCounts = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/stores/archived`, { headers: getAuthHeaders() });
      setCounts(res.data?.counts || { personal: 0, company: 0, total: 0 });
    } catch (err) {
      // counts default to 0 — fail-soft
    }
    // iter-259 — archived merchant accounts count
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/merchant-users/archived`, { headers: getAuthHeaders() });
      setAccountsCount(r.data?.count || 0);
    } catch (_e) { /* fail-soft */ }
  }, []);

  useEffect(() => { loadCounts(); }, [loadCounts]);

  const CARDS = [
    {
      id: 'personal',
      nameAr: 'أرشيف المتاجر الشخصية',
      nameEn: 'Personal Stores Archive',
      descAr: 'سجلات المتاجر الشخصية المؤرشفة (للاستعادة أو الحذف النهائي)',
      descEn: 'Archived personal stores (restore or hard-delete)',
      icon: User,
      color: '#a855f7',
      route: '/control/archive/cloudgm-business/personal',
      count: counts.personal,
    },
    {
      id: 'company',
      nameAr: 'أرشيف متاجر الشركات',
      nameEn: 'Company Stores Archive',
      descAr: 'سجلات متاجر الشركات المؤرشفة (للاستعادة أو الحذف النهائي)',
      descEn: 'Archived company stores (restore or hard-delete)',
      icon: Building2,
      color: '#14b8a6',
      route: '/control/archive/cloudgm-business/company',
      count: counts.company,
    },
    // iter-259 — archived merchant accounts
    {
      id: 'accounts',
      nameAr: 'الحسابات المؤرشفة',
      nameEn: 'Archived Accounts',
      descAr: 'استعد حسابات التجار المؤرشفة أو احذفها نهائياً',
      descEn: 'Restore archived merchant accounts or delete them permanently',
      icon: Users,
      color: '#8B5CF6',
      route: '/control/archive/cloudgm-business/accounts',
      count: accountsCount,
    },
  ];

  return (
    <DashboardLayout
      title="أرشيف CLOUDGM Business"
      titleEn="CLOUDGM Business Archive"
      subtitle="اختر فئة الأرشيف التي تريد إدارتها"
      subtitleEn="Pick the archive category you want to manage"
      screenCode="ARC-CGB"
      backPath="/control/archive"
      onBack={() => navigate('/control/archive')}
      breadcrumb={[
        { labelAr: 'الأرشيف', labelEn: 'Archive', path: '/control/archive' },
        { labelAr: 'CLOUDGM Business', labelEn: 'CLOUDGM Business' },
      ]}
    >
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="cloudgm-business-archive">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {visiblePages(CARDS, 'route').map((card, i) => {
            const Icon = card.icon;
            return (
              <motion.button
                key={card.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                whileHover={{ y: -5 }}
                onClick={() => navigate(card.route)}
                className="group relative bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-start transition-all hover:border-[#d4af37]/40"
                data-testid={`archive-card-stores-${card.id}`}
              >
                <div
                  className="w-14 h-14 rounded-xl mb-3 flex items-center justify-center border"
                  style={{ backgroundColor: `${card.color}15`, borderColor: `${card.color}40` }}
                >
                  <Icon className="h-7 w-7" style={{ color: card.color }} />
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-base font-bold text-white">
                    {language === 'ar' ? card.nameAr : card.nameEn}
                  </h3>
                  {card.count > 0 && (
                    <span
                      className="px-2 py-0.5 rounded-full text-xs font-bold"
                      style={{ backgroundColor: `${card.color}25`, color: card.color }}
                    >
                      {card.count}
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#71717A] leading-relaxed">
                  {language === 'ar' ? card.descAr : card.descEn}
                </p>
              </motion.button>
            );
          })}
        </div>
      </section>
    </DashboardLayout>
  );
}
